import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { ThreadData } from '@liveblocks/client'
import { useCreateThread } from '@liveblocks/react'
import _ from 'lodash'
import { AlertCircleIcon, CheckCheckIcon, ClockIcon, InfoIcon, LockOpenIcon } from 'lucide-react'
import { useMemo } from 'react'

import { useGetControl } from '@/api/control.hook'
import { useOptimisticSubmitRiskQuestionnaireThreadActivity } from '@/api/optimistic-submit-risk-questionnaire-thread-activity.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { SmartQuestionStatus } from '@/gen/inventory/v1/control_service_pb'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { relativeTimeFormatted, sortByTimestamp } from '@/lib/date/date'

import { RISK_QUESTIONNAIRE_THREAD_LABELS } from '@/pages/control/labels'
import type { RiskQuestionnaireThreadCardProps } from '@/pages/control/risk-questionnaire-thread-card/risk-questionnaire-thread-card'
import { RiskQuestionnaireTimelineItem } from '@/pages/control/risk-questionnaire-thread-card/risk-questionnaire-timeline-item'
import type { ThreadTimelineEntry } from '@/pages/control/risk-questionnaire-thread-card/types'
import {
  carriesDecision,
  groupConsecutiveEvaluations,
  statusChangesById,
  timelineEntryKey,
} from '@/pages/control/risk-questionnaire-thread-card/utils'

import { ControlCommentComposer } from '@/components/control-comments/control-comment-composer'
import { Alert, AlertIcon } from '@/components/lemmala-design-system/alert/alert'
import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { COMMENT_EVENT_SOURCES } from '@/components/shared/comment/consts'
import { serializeCommentBody } from '@/components/shared/comment/serialize-comment-body'
import { CommentMetadata } from '@/components/shared/comment/types'
import { useSendCommentNotification } from '@/components/shared/comment/use-send-comment-notification'
import { mentionEventProps } from '@/components/shared/comment/utils'

type CommentSlotKind = 'comment' | 'decision'

const placeCommentSlots = (
  entries: ThreadTimelineEntry[],
  slots: ReadonlyMap<string, CommentSlotKind>,
  liveThreadById: ReadonlyMap<string, ThreadData<CommentMetadata>>,
): ThreadTimelineEntry[] =>
  entries.flatMap((entry): ThreadTimelineEntry[] => {
    if (entry.kind !== 'activity') return [entry]
    const kind = slots.get(entry.activity.id)
    if (!kind) return [entry]
    const threadId = entry.activity.liveblocksThreadId
    const thread = threadId ? liveThreadById.get(threadId) : undefined
    const slot: ThreadTimelineEntry = { kind: 'commentSlot', activity: entry.activity, thread }
    if (kind === 'decision') return [slot, entry]
    return [slot]
  })

type ThreadCardContentProps = RiskQuestionnaireThreadCardProps & {
  // Undefined when the room is not mounted. Pending copies are skeletons; a failed load renders the stored activity.
  liveThreads?: ThreadData<CommentMetadata>[]
  liveThreadsPending?: boolean
  onCreateThread?: ReturnType<typeof useCreateThread>
}

export const ThreadCardContent = ({
  controlStatusId,
  thread,
  readOnly,
  liveThreads,
  liveThreadsPending = false,
  onCreateThread,
}: ThreadCardContentProps) => {
  const { mutate: submitRiskQuestionnaireThreadActivity } =
    useOptimisticSubmitRiskQuestionnaireThreadActivity()
  const sendMentionNotification = useSendCommentNotification()
  const trackCommentAdded = useTrackCallback('assessment.comment.added')
  const { data: control } = useGetControl(controlStatusId)
  const smartQuestion = control?.control?.smartQuestion

  const trackingProps: EventProperties = useMemo(
    () => ({
      assessment_id: control?.control?.ongoingAssessmentId,
      control_id: controlStatusId,
      company_id: control?.company?.companyId,
      company: control?.company?.profileName,
      source: COMMENT_EVENT_SOURCES.CONTROL_TIMELINE,
    }),
    [
      control?.control?.ongoingAssessmentId,
      controlStatusId,
      control?.company?.companyId,
      control?.company?.profileName,
    ],
  )

  const lastAnalysisResultId = useMemo(
    () => _.first(sortByTimestamp(thread.analysisResults, (item) => item.createdAt))?.id,
    [thread.analysisResults],
  )

  const timelineEntries = useMemo(() => {
    // A stored comment with a live thread keeps its row the whole time. While threads are
    // loading that row is a skeleton, so a status row already on screen does not jump down.
    const waitingForThreads = liveThreadsPending
    const liveThreadById = new Map((liveThreads ?? []).map((item) => [item.id, item]))
    const slots = new Map<string, CommentSlotKind>()
    const heldThreadIds = new Set<string>()
    const activities = thread.activities.flatMap((activity) => {
      if (activity.questionnaireStatus !== undefined) return []
      const threadId = activity.liveblocksThreadId
      const hasComment = !!activity.comment?.trim()
      if (!threadId || !hasComment) return [activity]
      const liveThread = liveThreadById.get(threadId)
      if (liveThread || waitingForThreads) {
        heldThreadIds.add(threadId)
        if (carriesDecision(activity)) {
          slots.set(activity.id, 'decision')
          return [{ ...activity, comment: undefined }]
        }
        slots.set(activity.id, 'comment')
        return [activity]
      }
      // Room or threads failed before they could replace this copy. Show the stored activity.
      if (liveThreads === undefined) return [activity]
      return carriesDecision(activity) ? [{ ...activity, comment: undefined }] : []
    })
    const sortedTimelineItems = sortByTimestamp(
      [
        ...thread.analysisResults,
        ...activities,
        ...(liveThreads ?? []).filter((item) => !heldThreadIds.has(item.id)),
      ],
      (item) => item.createdAt,
    )
    const entries = groupConsecutiveEvaluations(sortedTimelineItems)
    return placeCommentSlots(entries, slots, liveThreadById)
  }, [thread.analysisResults, thread.activities, liveThreads, liveThreadsPending])

  const statusChanges = useMemo(
    () => statusChangesById(thread.analysisResults),
    [thread.analysisResults],
  )

  // Derive the resolved state from the thread activities, mirroring the server logic.,
  // this keeps the checkbox and "Resolved" alert in sync with the activity item that appears
  // immediately, instead of lagging behind the slower smartQuestion.status refetch.
  const wasMarkedAsResolved = useMemo(() => {
    const latestResolution = _.first(
      sortByTimestamp(
        thread.activities.filter((activity) => activity.markedAsResolved !== undefined),
        (activity) => activity.createdAt,
      ),
    )
    return latestResolution?.markedAsResolved === true
  }, [thread.activities])

  // The questionnaire's share time is the earliest activity, not thread.time (which the server
  // sets to the latest activity for timeline ordering).
  const sharedAt = useMemo(() => {
    const sortedStatusChanges = sortByTimestamp(
      thread.activities.filter((activity) => activity.questionnaireStatus !== undefined),
      (activity) => activity.createdAt,
    )

    return sortedStatusChanges.find(
      (statusChange) => statusChange.questionnaireStatus === QuestionnaireStatus.ONGOING,
    )?.createdAt
  }, [thread.activities])

  const isValidated = control?.analysisResult?.status === ControlStatus.VALIDATED

  return (
    <div className='rounded border shadow'>
      <div className='flex items-center gap-2 rounded-t bg-main-background-muted px-3 py-2'>
        <div className='text-nowrap text-main-foreground-primary text-label-default'>
          {RISK_QUESTIONNAIRE_THREAD_LABELS.TITLE}
        </div>
        <div className='flex w-full items-center justify-between gap-2'>
          <div className='text-main-foreground-tertiary text-body-small'>
            {RISK_QUESTIONNAIRE_THREAD_LABELS.SHARED_PREFIX}{' '}
            {relativeTimeFormatted(sharedAt ? timestampDate(sharedAt) : new Date())}
          </div>
          <div className='flex items-center gap-1.5'>
            <CheckboxWithLabel
              // Disable the checkbox if the control is validated
              checked={isValidated || wasMarkedAsResolved}
              disabled={isValidated || readOnly}
              onCheckedChange={(checked) =>
                submitRiskQuestionnaireThreadActivity({
                  controlStatusId: controlStatusId,
                  isResolved: checked === true,
                })
              }
            >
              {RISK_QUESTIONNAIRE_THREAD_LABELS.MARK_AS_RESOLVED}
            </CheckboxWithLabel>
            <Tooltip className='w-40' trigger={<InfoIcon className='size-4' />}>
              {isValidated
                ? RISK_QUESTIONNAIRE_THREAD_LABELS.VALIDATED_TOOLTIP
                : RISK_QUESTIONNAIRE_THREAD_LABELS.MARK_AS_RESOLVED_TOOLTIP}
            </Tooltip>
          </div>
        </div>
      </div>
      {!wasMarkedAsResolved && smartQuestion?.status === SmartQuestionStatus.AWAITING_REVIEW && (
        <div className='px-3 py-2'>
          <Alert
            icon={<AlertIcon icon={AlertCircleIcon} />}
            variant='attention'
            alertTitle={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_REVIEW_TITLE}
            description={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_REVIEW_DESCRIPTION}
          />
        </div>
      )}
      {!wasMarkedAsResolved && smartQuestion?.status === SmartQuestionStatus.AWAITING_RECIPIENT && (
        <div className='px-3 py-2'>
          <Alert
            icon={<AlertIcon icon={ClockIcon} />}
            variant='info'
            alertTitle={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_RECIPIENT_TITLE}
            description={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_RECIPIENT_DESCRIPTION}
          />
        </div>
      )}
      {!wasMarkedAsResolved && smartQuestion?.status === SmartQuestionStatus.AWAITING_REOPEN && (
        <div className='px-3 py-2'>
          <Alert
            icon={<AlertIcon icon={LockOpenIcon} />}
            variant='warning'
            alertTitle={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_REOPEN_TITLE}
            description={RISK_QUESTIONNAIRE_THREAD_LABELS.AWAITING_REOPEN_DESCRIPTION}
          />
        </div>
      )}
      {wasMarkedAsResolved && (
        <div className='px-3 py-2'>
          <Alert
            icon={<AlertIcon icon={CheckCheckIcon} />}
            variant='default'
            alertTitle={RISK_QUESTIONNAIRE_THREAD_LABELS.RESOLVED_TITLE}
            description={RISK_QUESTIONNAIRE_THREAD_LABELS.RESOLVED_DESCRIPTION}
          />
        </div>
      )}
      {timelineEntries.map((entry) => (
        <div key={timelineEntryKey(entry)} className='m-3'>
          <RiskQuestionnaireTimelineItem
            entry={entry}
            lastAnalysisResultId={lastAnalysisResultId}
            statusChanges={statusChanges}
            readOnly={readOnly}
            trackingProps={trackingProps}
          />
        </div>
      ))}
      {control?.control?.ongoingAssessmentId && !readOnly && (
        <div className='p-4'>
          <ControlCommentComposer
            audience='external'
            placeholder={RISK_QUESTIONNAIRE_THREAD_LABELS.ADD_COMMENT_PLACEHOLDER}
            onSubmit={async (comment) => {
              const text = (await serializeCommentBody(comment.body)).trim()
              if (!text) return
              const thread = onCreateThread?.({
                body: comment.body,
                metadata: { controlId: controlStatusId, audience: 'external' },
              })
              submitRiskQuestionnaireThreadActivity({
                comment: text,
                controlStatusId: controlStatusId,
                liveblocksThreadId: thread?.id,
              })
              trackCommentAdded({ ...trackingProps, ...mentionEventProps(comment.body) })
              sendMentionNotification({
                assessmentId: control?.control?.ongoingAssessmentId,
                controlId: controlStatusId,
                body: comment.body,
                trackingProps,
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
